import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { PRACTICE_VERTICALS, PracticeVertical } from '../../components/onboarding-flow-components/constants';
import { StackedCardWizardStep } from '../../components/onboarding-flow-components/stacked-card-wizard/stacked-card-wizard-step-compound';
import { useStepData } from '../../components/onboarding-flow-components/stacked-card-wizard/use-step-data';
import { VerticalSelectionStep } from '../../components/onboarding-flow-components/vertical-selection-step';
import { useOnboardingFlowInfo } from '../../hooks/onboarding-flow-hooks/use-onboarding-flow-info';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { trackOnboardingFlow } from '../../utils/track-onboarding-flow';
import { OnboardingStep, VerticalStepData } from './types';
import { getDefaultIndustry, getDefaultVertical } from './utils';

export const PracticeVerticalStep = () => {
  const { t } = useTranslation('schedule-onboarding');
  const { selectedLocationIds, accessibleLocationData } = useSchedulingLocationInfo();
  const { context } = useSettingsNavigate<{ calendarLocationIds?: string[] }>();
  const locationIds = context?.calendarLocationIds?.length
    ? context.calendarLocationIds.map((id) => id)
    : selectedLocationIds.map((id) => id);

  const { onboardingFlowInfo, setOnboardingFlowInfo } = useOnboardingFlowInfo();
  const { mergeData, getData } = useStepData<VerticalStepData>(OnboardingStep.Vertical);
  const stepData = getData();

  const [industryName, setIndustryName] = useState(() =>
    getDefaultIndustry({
      stepperIndustry: stepData.industry,
      userPreferenceVertical: onboardingFlowInfo.practiceVertical || '',
    })
  );

  const [selectedVertical, setSelectedVertical] = useState<PracticeVertical | undefined>(() =>
    getDefaultVertical({
      locationId: selectedLocationIds[0],
      locationData: accessibleLocationData,
      stepperVertical: stepData.practiceVertical,
      userPreferenceVertical: onboardingFlowInfo.practiceVertical || '',
    })
  );

  const onNextClick = async () => {
    if (!selectedVertical) return;
    mergeData({ practiceVertical: selectedVertical, industry: industryName });

    // set user preference
    if (selectedVertical !== onboardingFlowInfo.practiceVertical) {
      setOnboardingFlowInfo({
        practiceVertical: selectedVertical === PracticeVertical.Other ? industryName : selectedVertical,
        // Set locationId only if one location is selected (location step is not shown in this case)
        ...(locationIds.length === 1 && { locationId: locationIds[0] }),
      });
    }
    trackOnboardingFlow('wizardVerticalStepCompleted');
  };

  return (
    <StackedCardWizardStep
      stepId={OnboardingStep.Vertical}
      canGoNext={!!(selectedVertical === PracticeVertical.Other ? industryName : selectedVertical)}
      onNext={onNextClick}
    >
      <StackedCardWizardStep.Header
        title={t('What type of practice do you run?')}
        description={t("We'll customize your setup based on your practice type.")}
      />

      <StackedCardWizardStep.Content>
        <VerticalSelectionStep
          verticalOptions={PRACTICE_VERTICALS}
          selectedVertical={selectedVertical}
          industryName={industryName}
          setPracticeVertical={(value) => setSelectedVertical(value)}
          setIndustryName={(value) => setIndustryName(value)}
        />
      </StackedCardWizardStep.Content>

      <StackedCardWizardStep.Footer />
    </StackedCardWizardStep>
  );
};
